"use client";

import { motion } from "framer-motion";
import type { Locale } from "@/config/i18n";
import { CHAUFFEUR_SLUG_UI } from "./chauffeurSlugUi";
import { tr } from "@/config/localeText";

type ProcessTimelineSectionProps = {
  heading: string;
  steps: string[];
  locale?: Locale;
};

export default function ProcessTimelineSection({
  heading,
  steps,
  locale = "fr",
}: ProcessTimelineSectionProps): JSX.Element {
  if (!steps?.length) return <></>;
  const ui = tr(CHAUFFEUR_SLUG_UI, locale);

  return (
    <section className="relative overflow-hidden bg-white py-14 text-black">
      <div className="pointer-events-none absolute -right-14 top-10 h-44 w-44 rounded-full bg-primary/10 blur-[80px]" />
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <motion.p
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.4 }}
          className="mb-2 text-xs uppercase tracking-[0.18em] text-black/55"
        >
          {ui.processEyebrow}
        </motion.p>
        <motion.h2
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="mb-5 font-space-age text-xl tracking-[0.2em]"
        >
          {heading}
        </motion.h2>
        <ol className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <motion.li
              key={step}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
              whileHover={{ y: -3 }}
              className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/35"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-black/50">
                {ui.stepWord} {index + 1}
              </p>
              <p className="mt-2 text-sm text-black/80">{step}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
